import React, { useState } from "react";
import { gql } from "@apollo/client";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import { Button } from "@src/lib/ui/Button";
import { Input } from "@src/lib/ui/Input";
import { DictationTextarea } from "@src/features/aiAssist/DictationButton";
import Text from "@src/lib/ui/Text";
import { OpusAgentFieldsFragment } from "@src/features/opus-agent/agentPanel.generated";
import {
  OpusAgentAvatarInput,
  OpusAgentAvatarKind,
} from "@src/types.generated";
import { agentColor, agentImageUrl, randomAgentColor } from "./AgentAvatar";
import AgentAvatarPicker, { AgentAvatarChoice } from "./AgentAvatarPicker";
import { agentBotShape } from "./botOrb";
import { BOT_SHAPES } from "./botShapes";
import {
  useClearOpusAgentScheduleMutation,
  useCreateOpusAgentMutation,
  useSetOpusAgentScheduleMutation,
  useUpdateOpusAgentMutation,
} from "./AgentDialog.generated";
import AgentScheduleFields from "./AgentScheduleFields";
import {
  EMPTY_SCHEDULE,
  ScheduleDraft,
  rruleToSchedule,
  scheduleToRrule,
} from "./agentSchedule";

export const AGENT_NAME_MAX_LENGTH = 120;
// Mirrors scadmin's OPUS_AGENT_SUGGESTED_QUESTIONS_MAX_COUNT / _MAX_LENGTH.
export const AGENT_SUGGESTED_QUESTIONS_MAX_COUNT = 6;
export const AGENT_SUGGESTED_QUESTION_MAX_LENGTH = 200;
// OpusAgentInput still requires an emoji; agents show their bot, not it.
const DEFAULT_EMOJI = "🤖";

// The agent's current avatar; a new agent starts on a random colour.
const initialAvatar = (
  agent: OpusAgentFieldsFragment | null,
): AgentAvatarChoice => {
  if (!agent) return { kind: "color", color: randomAgentColor() };
  const url = agentImageUrl(agent);
  return url
    ? { kind: "image", url }
    : { kind: "color", color: agentColor(agent) };
};

const avatarInput = (
  avatar: AgentAvatarChoice,
  agent: OpusAgentFieldsFragment | null,
): OpusAgentAvatarInput =>
  avatar.kind === "image"
    ? { kind: OpusAgentAvatarKind.Image, imageUrl: avatar.url }
    : {
        kind: OpusAgentAvatarKind.Bot,
        // Shapes are stored but not drawn: keep the agent's, or give a new
        // one a random one, so agents still differ if shapes come back.
        botShape: agent
          ? agentBotShape(agent)
          : BOT_SHAPES[Math.floor(Math.random() * BOT_SHAPES.length)],
        botColor: avatar.color,
      };

const sameAvatar = (a: AgentAvatarChoice, b: AgentAvatarChoice) =>
  a.kind === "image"
    ? b.kind === "image" && a.url === b.url
    : b.kind === "color" && a.color === b.color;

type Props = {
  agent: OpusAgentFieldsFragment | null;
  onClose: () => void;
  onCreated?: (agent: OpusAgentFieldsFragment) => void;
};

const AgentDialog: React.FC<Props> = ({ agent, onClose, onCreated }) => {
  const [name, setName] = useState(agent?.name ?? "");
  const [instructions, setInstructions] = useState(agent?.instructions ?? "");
  const emoji = agent?.emoji ?? DEFAULT_EMOJI;
  const [initial] = useState(() => initialAvatar(agent));
  const [avatar, setAvatar] = useState(initial);
  // An upload or generation under way: Save waits for its picture.
  const [avatarBusy, setAvatarBusy] = useState(false);
  // Edited one per line; blank and repeated lines are dropped on save.
  const [questionsText, setQuestionsText] = useState(
    (agent?.suggestedQuestions ?? []).join("\n"),
  );
  const suggestedQuestions = [
    ...new Set(
      questionsText
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean),
    ),
  ];
  const questionsError =
    suggestedQuestions.length > AGENT_SUGGESTED_QUESTIONS_MAX_COUNT
      ? `Add up to ${AGENT_SUGGESTED_QUESTIONS_MAX_COUNT} questions.`
      : suggestedQuestions.some(
            (q) => q.length > AGENT_SUGGESTED_QUESTION_MAX_LENGTH,
          )
        ? `Keep each question to ${AGENT_SUGGESTED_QUESTION_MAX_LENGTH} characters or fewer.`
        : null;
  const savedSchedule = agent?.schedule ?? null;
  const [persistedId, setPersistedId] = useState<string | null>(
    agent?.id ?? null,
  );
  const [createdAgent, setCreatedAgent] =
    useState<OpusAgentFieldsFragment | null>(null);
  const [useSimple, setUseSimple] = useState(false);
  const [schedule, setSchedule] = useState<ScheduleDraft>(() =>
    savedSchedule
      ? rruleToSchedule(
          savedSchedule.rrule,
          savedSchedule.prompt,
          savedSchedule.enabled,
        ) ?? {
          ...EMPTY_SCHEDULE,
          enabled: savedSchedule.enabled,
          prompt: savedSchedule.prompt,
        }
      : EMPTY_SCHEDULE,
  );
  const customRule =
    savedSchedule &&
    !rruleToSchedule(
      savedSchedule.rrule,
      savedSchedule.prompt,
      savedSchedule.enabled,
    ) &&
    !useSimple
      ? savedSchedule.rrule
      : null;
  const [error, setError] = useState<string | null>(null);
  const [createAgent, { loading: creating }] = useCreateOpusAgentMutation({
    refetchQueries: ["OpusAgents"],
  });
  const [updateAgent, { loading: updating }] = useUpdateOpusAgentMutation();
  const [setAgentSchedule, { loading: scheduling }] =
    useSetOpusAgentScheduleMutation({ refetchQueries: ["OpusAgents"] });
  const [clearAgentSchedule, { loading: clearing }] =
    useClearOpusAgentScheduleMutation({ refetchQueries: ["OpusAgents"] });

  const submit = async () => {
    const input = {
      name: name.trim(),
      emoji,
      instructions,
      suggestedQuestions,
      // Sent on create, and on edit only when changed (an untouched agent
      // keeps its default, which follows its template).
      ...(!agent || !sameAvatar(avatar, initial)
        ? { avatar: avatarInput(avatar, agent) }
        : {}),
    };
    setError(null);
    try {
      if (persistedId) {
        const result = await updateAgent({
          variables: { id: persistedId, input },
        });
        const mutation = result.data?.updateOpusAgent;
        if (!mutation?.success) {
          setError(
            mutation?.error?.message ??
              "Couldn't save the agent. Please try again.",
          );
          return;
        }
        if (!(await saveSchedule(persistedId))) return;
        if (createdAgent) onCreated?.(createdAgent);
      } else {
        const result = await createAgent({ variables: { input } });
        const mutation = result.data?.createOpusAgent;
        if (!mutation?.success) {
          setError(
            mutation?.error?.message ??
              "Couldn't save the agent. Please try again.",
          );
          return;
        }
        const created = mutation.agent;
        if (created) {
          setPersistedId(created.id);
          setCreatedAgent(created);
          const scheduled = await saveSchedule(created.id);
          if (!scheduled) return;
          onCreated?.(created);
        }
      }
    } catch {
      setError("Couldn't save the agent. Please try again.");
      return;
    }
    onClose();
  };

  const saveSchedule = async (agentId: string): Promise<boolean> => {
    const nextRrule = customRule ?? scheduleToRrule(schedule);
    const sameRule = (left: string, right: string) =>
      left.replace(/;BYSECOND=0/g, "") === right.replace(/;BYSECOND=0/g, "");
    const unchanged =
      !schedule.enabled && !savedSchedule
        ? true
        : Boolean(savedSchedule?.enabled) === schedule.enabled &&
          savedSchedule?.prompt === schedule.prompt.trim() &&
          sameRule(savedSchedule?.rrule ?? "", nextRrule);
    if (unchanged) return true;
    if (schedule.enabled) {
      if (!schedule.prompt.trim()) {
        setError("Say what the agent should do each run.");
        return false;
      }
      const result = await setAgentSchedule({
        variables: {
          id: agentId,
          input: {
            rrule: nextRrule,
            prompt: schedule.prompt.trim(),
            enabled: true,
            timezone: savedSchedule?.tzid,
          },
        },
      });
      const mutation = result.data?.setOpusAgentSchedule;
      if (!mutation?.success) {
        setError(
          mutation?.error?.message ??
            "Couldn't save the schedule. Please try again.",
        );
        return false;
      }
      return true;
    }
    if (savedSchedule) {
      const result = await clearAgentSchedule({ variables: { id: agentId } });
      if (!result.data?.clearOpusAgentSchedule?.success) {
        setError("Couldn't turn the schedule off. Please try again.");
        return false;
      }
    }
    return true;
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="flex max-h-[90vh] flex-col gap-0 p-0"
        data-testid="agent-dialog"
      >
        <DialogHeader className="items-center border-b border-border-primary px-5 py-3">
          <DialogTitle asChild>
            <Text variant="compact-title">
              {agent ? "Edit agent" : "New agent"}
            </Text>
          </DialogTitle>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
          {!agent && (
            <Text variant="compact-description" color="muted" multiline>
              An agent is Ask Opus with standing instructions.
            </Text>
          )}
          <Input
            label="Name"
            value={name}
            maxLength={AGENT_NAME_MAX_LENGTH}
            placeholder="Compliance Auditor"
            autoFocus
            onChange={(event) => setName(event.target.value)}
            data-testid="agent-name-input"
          />
          <AgentAvatarPicker
            value={avatar}
            onChange={setAvatar}
            onBusyChange={setAvatarBusy}
            agentName={name}
          />
          <div className="flex flex-col gap-1.5">
            <label htmlFor="agent-instructions">
              <Text variant="compact-title">Instructions</Text>
            </label>
            <DictationTextarea
              id="agent-instructions"
              value={instructions}
              minRows={8}
              maxRows={16}
              autoSize
              placeholder="What should this agent focus on? These are added to Ask Opus's instructions for this agent."
              onValueChange={setInstructions}
              data-testid="agent-instructions-input"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="agent-suggested-questions">
              <Text variant="compact-title">Suggested questions</Text>
            </label>
            <DictationTextarea
              id="agent-suggested-questions"
              value={questionsText}
              minRows={3}
              maxRows={8}
              autoSize
              placeholder="One per line. Shown as starters in an empty chat with this agent."
              onValueChange={setQuestionsText}
              data-testid="agent-suggested-questions-input"
            />
            {questionsError && (
              <Text
                variant="compact-description"
                className="text-status-error-text"
                data-testid="agent-suggested-questions-error"
              >
                {questionsError}
              </Text>
            )}
          </div>
          <AgentScheduleFields
            value={schedule}
            tzid={savedSchedule?.tzid ?? null}
            runsAsViewer={savedSchedule?.runsAsViewer ?? true}
            customRule={customRule}
            onChange={setSchedule}
            onUseSimple={() => setUseSimple(true)}
          />
        </div>
        {error && (
          <Text
            variant="compact-description"
            className="px-5 pb-3 text-status-error-text"
          >
            {error}
          </Text>
        )}
        <DialogFooter className="border-t border-border-primary p-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={
              !name.trim() ||
              Boolean(questionsError) ||
              avatarBusy ||
              creating ||
              updating ||
              scheduling ||
              clearing
            }
            data-testid="agent-dialog-submit"
          >
            {agent ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AgentDialog;

gql`
  mutation CreateOpusAgent($input: OpusAgentInput!) {
    createOpusAgent(input: $input) {
      success
      error {
        code
        message
      }
      agent {
        ...OpusAgentFields
      }
    }
  }

  mutation SetOpusAgentSchedule($id: UUID!, $input: OpusAgentScheduleInput!) {
    setOpusAgentSchedule(id: $id, input: $input) {
      success
      error {
        code
        message
      }
      agent {
        ...OpusAgentFields
      }
    }
  }

  mutation ClearOpusAgentSchedule($id: UUID!) {
    clearOpusAgentSchedule(id: $id) {
      success
      error {
        code
        message
      }
      agent {
        ...OpusAgentFields
      }
    }
  }

  mutation UpdateOpusAgent($id: UUID!, $input: OpusAgentInput!) {
    updateOpusAgent(id: $id, input: $input) {
      success
      error {
        code
        message
      }
      agent {
        ...OpusAgentFields
      }
    }
  }
`;
